<script setup lang="ts">
/**
 * @fileoverview Root application component with tenant theme provider
 *
 * CSS custom properties are initialized with centralized defaults from
 * brandingDefaults.ts and dynamically updated by TenantThemeProvider.
 *
 * Gates rendering on both locale and branding being ready to prevent:
 * - i18n race condition with service worker
 * - FOUC (flash of unstyled content) from default green before custom colors load
 */
import { onMounted, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useDeveloperStore } from '@/stores/developer'
import { localeReady } from '@/plugins/i18n'
import TenantThemeProvider from '@/components/tenant/TenantThemeProvider.vue'
import PlatformThemeProvider from '@/components/common/PlatformThemeProvider.vue'
import PinGuard from '@/components/common/PinGuard.vue'

const authStore = useAuthStore()
const developerStore = useDeveloperStore()

/**
 * Gate on BOTH locale AND branding being ready
 * Prevents FOUC by ensuring custom colors are loaded before rendering
 */
const appReady = computed(() => localeReady.value && developerStore.isReady)

onMounted(() => {
  authStore.init()
  developerStore.init()
})
</script>

<template>
  <v-app>
    <template v-if="appReady">
      <PlatformThemeProvider>
        <PinGuard>
          <TenantThemeProvider>
            <router-view />
          </TenantThemeProvider>
        </PinGuard>
      </PlatformThemeProvider>
    </template>
    <template v-else>
      <v-overlay :model-value="true" class="align-center justify-center">
        <v-progress-circular indeterminate size="64" color="grey-darken-1" />
      </v-overlay>
    </template>
  </v-app>
</template>

<style>
/* Global styles - minimal, Vuetify handles most */
html {
  overflow-y: auto !important;
}

/**
 * CSS custom properties for branding
 *
 * These values match the centralized defaults in src/config/brandingDefaults.ts
 * and are dynamically updated by TenantThemeProvider at runtime.
 *
 * @see src/config/brandingDefaults.ts for source of truth
 */
:root {
  /* Brand Colors - Reference Vuetify theme tokens */
  --color-primary: rgb(var(--v-theme-primary));
  --color-primary-darken-1: rgb(var(--v-theme-primary-darken-1, var(--v-theme-primary)));
  --color-primary-darken-2: rgb(var(--v-theme-primary-darken-2, var(--v-theme-primary)));
  --color-primary-lighten-1: rgb(var(--v-theme-primary-lighten-1, var(--v-theme-primary)));
  --color-secondary: rgb(var(--v-theme-secondary));
  --color-secondary-darken-1: rgb(var(--v-theme-secondary-darken-1, var(--v-theme-secondary)));
  --color-secondary-lighten-1: rgb(var(--v-theme-secondary-lighten-1, var(--v-theme-secondary)));
  --color-accent: rgb(var(--v-theme-accent, 255, 204, 0));

  /* Semantic Colors - Reference Vuetify theme tokens */
  --color-info: rgb(var(--v-theme-info));
  --color-success: rgb(var(--v-theme-success));
  --color-warning: rgb(var(--v-theme-warning));
  --color-error: rgb(var(--v-theme-error));

  /* Surface Colors */
  --color-background: rgb(var(--v-theme-background));
  --color-surface: rgb(var(--v-theme-surface));
  --color-surface-bright: rgb(var(--v-theme-surface-bright));
  --color-surface-light: rgb(var(--v-theme-surface-light));
  --color-surface-variant: rgb(var(--v-theme-surface-variant));

  /* Text Colors */
  --color-on-primary: rgb(var(--v-theme-on-primary));
  --color-on-secondary: rgb(var(--v-theme-on-secondary));
  --color-on-background: rgb(var(--v-theme-on-background));
  --color-on-surface: rgb(var(--v-theme-on-surface));
  --color-on-surface-variant: rgb(var(--v-theme-on-surface-variant));
  --color-text-secondary: rgb(var(--v-theme-on-surface-variant));

  /* Footer - Uses surface theme tokens for consistency */
  --color-footer-background: rgb(var(--v-theme-surface));
  --color-footer-on-background: rgb(var(--v-theme-on-surface));

  /* Typography (from FONT_DEFAULTS) */
  --font-heading: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  --font-body: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: 'JetBrains Mono', ui-monospace, SFMono-Regular, monospace;

  /* Spacing (from SPACING_DEFAULTS) */
  --spacing-xs: 4px;
  --spacing-sm: 8px;
  --spacing-md: 16px;
  --spacing-lg: 24px;
  --spacing-xl: 32px;
  --spacing-xxl: 48px;

  /* Border Radius (from BORDER_DEFAULTS) */
  --radius-sm: 4px;
  --radius-md: 8px;
  --radius-lg: 12px;
  --radius-xl: 16px;
  --radius-pill: 9999px;

  /* Transitions (from TRANSITION_DEFAULTS) */
  --transition-fast: 150ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-normal: 250ms cubic-bezier(0.4, 0, 0.2, 1);
  --transition-slow: 400ms cubic-bezier(0.4, 0, 0.2, 1);

  /* Z-Index (from Z_INDEX_DEFAULTS) */
  --z-dropdown: 1000;
  --z-sticky: 1020;
  --z-fixed: 1030;
  --z-modal-backdrop: 1040;
  --z-modal: 1050;
  --z-popover: 1060;
  --z-tooltip: 1070;
  --z-snackbar: 1080;
  --z-loading: 9999;
}

/**
 * Reduced motion support for accessibility (WCAG 2.1 AA)
 * Respects user's prefers-reduced-motion system setting
 */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}
</style>
